import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { ADDONS, atLeast, SECTION_LEVEL, SHOP_PLANS, type Addon, type Level } from '@fsp/core';
import { api, type Me } from '../api';
import { money } from '../form';
import { errorText, useI18n } from '../i18n';
import { Icon, type IconName } from '../icons';
import { Alert, LEVEL_NAMES, PageHead, PanelShell, selfServe } from '../layout';
import { FIRM_ONLY, IV_ONLY } from '../nav';

type States = Record<string, { enabled: boolean; interested: boolean }>;
type Filter = 'all' | 'on' | 'soon';

/** Parduotuvė: what each plan has (and what is coming), and the add-ons the company switches on or off. */
export function ShopPage({ me }: { me: Me }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<Filter>('all');
  const shop = useQuery({ queryKey: ['shop-all'], queryFn: () => api.get<{ addons: States; unavailable: string[] }>('api/v1/shop') });
  const states = { data: shop.data?.addons };
  const unavailable = shop.data?.unavailable ?? [];
  const act = useMutation({
    mutationFn: ({ addon, path, body }: { addon: string; path: string; body: object }) => api.put<{ addons: States }>(`api/v1/shop/addons/${addon}${path}`, body),
    onSuccess: (r) => {
      void queryClient.invalidateQueries({ queryKey: ['shop-all'] });
      void queryClient.invalidateQueries({ queryKey: ['me'] }); // the menu follows
    },
  });
  // Opened for one add-on (e.g. "+ Kreditas" → #ai-assistant): scroll to it and mark it.
  const hash = useRouterState({ select: (st) => st.location.hash });
  useEffect(() => {
    if (!hash || !shop.data) return;
    const el = document.getElementById(hash);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el?.classList.add('is-target');
  }, [hash, shop.data]);
  const owner = me.company.role === 'owner' && !me.viewAsAdmin;
  const current = me.subscription?.plan ?? 'premium';
  const level: Level = me.subscription?.level ?? 'premium';
  const s = states.data;
  const statusOf = (a: Addon) => (unavailable.includes(a.id) ? 'soon' : a.status);
  // Individuali veikla's own pages are only for a person working on their own.
  const shown = ADDONS.filter((a) => !(a.module && IV_ONLY.includes(a.module) && me.company.kind !== 'iv') && !(a.module && FIRM_ONLY.includes(a.module) && me.subscription?.plan !== 'firm')).filter((a) => filter === 'all' || (filter === 'on' ? s?.[a.id]?.enabled : statusOf(a) === 'soon'));
  // Nothing is sold one by one: each section is in a plan (sections without one are in every plan).
  const levelOf = (a: Addon): Level => (a.module ? SECTION_LEVEL[a.module] : undefined) ?? (a.plan === 'basic' ? 'basic' : a.plan ? 'premium' : 'free');
  const inPlan = (a: Addon) => { const l = levelOf(a); return l === 'free' ? t('Visuose planuose') : t('{plan} plane', { plan: LEVEL_NAMES[l] }); };
  const filters: [Filter, string][] = [['all', 'Visi'], ['on', 'Įjungti'], ['soon', 'Netrukus']];
  const choose = owner && selfServe(me);

  return (
    <PanelShell me={me} head={<PageHead eyebrow={t('Parduotuvė')} title={t('Planai ir priedai')} text={t('Kas yra jūsų plane, kas netrukus atsiras ir kokius priedus galite įjungti.')} />}>
      <section className="shop-plans">
        {SHOP_PLANS.filter((p) => (me.company.kind === 'iv' ? ['free', 'iv', 'premium'] : ['free', 'basic', 'premium', 'full', 'firm']).includes(p.id)).map((p) => (
          <article key={p.id} className={`panel-card shop-plan${p.id === current ? ' is-current' : ''}${p.id === 'premium' ? ' is-featured' : ''}`}>
            <div className="shop-plan-head">
              <h2>{t(p.name)}</h2>
              {p.id === current ? <span className="shop-tag shop-tag--current">{me.subscription?.state === 'trial' ? t('Bandomasis laikotarpis') : t('Jūsų planas')}</span> : null}
            </div>
            <p className="shop-price">{p.priceCents === null ? t('Kaina pagal susitarimą') : p.priceCents === 0 ? t('Nemokamai')
              : p.perClient ? t('nuo {price} / klientas / mėn. + PVM', { price: money(p.priceCents) })
              : p.from ? t('nuo {price} / mėn. + PVM', { price: money(p.priceCents) }) : t('{price} / mėn. + PVM', { price: money(p.priceCents) })}</p>
            <p className="muted">{t(p.text)}</p>
            <ul className="shop-features">
              {p.features.map((f) => (
                <li key={f.text} className={f.status === 'soon' ? 'is-soon' : undefined}>
                  <span aria-hidden="true">{f.status === 'soon' ? '◌' : '✓'}</span>
                  <span>{t(f.text)}{f.status === 'soon' ? <> <span className="shop-tag">{t('Netrukus')}</span></> : null}</span>
                </li>
              ))}
            </ul>
            {p.id === current && me.subscription?.state !== 'trial' ? <Link to="/account" hash="plan" className="button button--ghost button--small">{t('Plano būsena')}</Link>
              : (p.id === 'basic' || p.id === 'premium' || p.id === 'iv' || p.id === 'firm') && choose ? <Link to="/account" hash="plan" className="button button--primary button--small">{t('Pasirinkti')}</Link>
              : p.id === 'full' ? <Link to="/support" className="button button--ghost button--small">{t('Susisiekti')}</Link> : null}
          </article>
        ))}
      </section>

      <section className="panel-card">
        <div className="card-head">
          <div><p className="eyebrow">{t('Priedai')}</p><h2>{t('Įjunkite tai, ko reikia')}</h2></div>
          <div className="calendar-view-switch" role="group" aria-label={t('Filtras')}>
            {filters.map(([key, label]) => (
              <a key={key} href={`#${key}`} className={filter === key ? 'active' : undefined} aria-pressed={filter === key} onClick={(e) => { e.preventDefault(); setFilter(key); }}>{t(label)}</a>
            ))}
          </div>
        </div>
        <p className="muted">{owner ? t('Priedai atskirai neparduodami: kiekvienas yra Basic arba Premium plane. Išjungtas priedas dingsta iš meniu; jo duomenys lieka ir grįžta įjungus.') : t('Priedus įjungia ir išjungia įmonės savininkas.')}</p>
        {act.isError ? <Alert kind="error">{errorText(t, act.error)}</Alert> : null}
        <div className="shop-addons">
          {shown.map((a) => {
            const state = s?.[a.id];
            return (
              <article key={a.id} id={a.id} className={`shop-addon${state?.enabled ? ' is-on' : ''}${statusOf(a) === 'soon' ? ' is-soon' : ''}`}>
                <span className="shop-addon-icon"><Icon name={a.icon as IconName} /></span>
                <div className="shop-addon-body">
                  <h3>{t(a.name)}</h3>
                  <p>{t(a.text)}</p>
                  <small>{inPlan(a)}{statusOf(a) === 'soon' ? <> · <span className="shop-tag">{t('Netrukus')}</span></> : null}</small>
                </div>
                <div className="shop-addon-action">
                  {statusOf(a) === 'available' && !atLeast(level, levelOf(a)) ? (
                    choose ? <Link to="/account" hash="plan" className="button button--small button--primary">{LEVEL_NAMES[levelOf(a)]}</Link> : <span className="section-lock">{LEVEL_NAMES[levelOf(a)]}</span>
                  ) : statusOf(a) === 'available' ? (
                    <label className="shop-switch" title={state?.enabled ? t('Išjungti') : t('Įjungti')}>
                      <input type="checkbox" role="switch" aria-label={t(a.name)} checked={Boolean(state?.enabled)} disabled={!owner || !state || act.isPending}
                        onChange={(e) => act.mutate({ addon: a.id, path: '', body: { enabled: e.target.checked } })} />
                      <span aria-hidden="true" /><em>{state?.enabled ? t('Įjungta') : t('Išjungta')}</em>
                    </label>
                  ) : (
                    <button type="button" className={`button button--small ${state?.interested ? 'button--ghost' : 'button--primary'}`} disabled={!state || act.isPending || Boolean(me.viewAsAdmin)}
                      onClick={() => act.mutate({ addon: a.id, path: '/interest', body: { interested: !state?.interested } })}>
                      {state?.interested ? t('✓ Pranešime') : t('Pranešti, kai bus')}
                    </button>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </PanelShell>
  );
}
